/**
 * Doctor onboarding form (A-03) — revealed by the Add button on the Doctors screen.
 *
 * Captures profile fields + the verification checklist as checkboxes (admin may tick items
 * at creation; activation is gated server-side on all items being checked).
 */

import { useState } from "react";

import { DOCTOR_CHECKLIST_ITEMS } from "../../api/admin";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { PrefixInput } from "../../components/ui/PrefixInput";

export interface DoctorFormValues {
  full_name: string;
  phone: string;
  email: string;
  state_medical_council: string;
  license_number: string;
  specialisation: string;
  verification_checklist: Record<string, boolean>;
}

// local@domain.tld — no spaces, one "@", and a TLD of at least 2 letters.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const EMPTY: DoctorFormValues = {
  full_name: "",
  phone: "+91",
  email: "",
  state_medical_council: "",
  license_number: "",
  specialisation: "",
  verification_checklist: {},
};

export function DoctorForm({
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  submitting: boolean;
  error: string | null;
  onSubmit: (values: DoctorFormValues) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<DoctorFormValues>(EMPTY);
  const set = (patch: Partial<DoctorFormValues>) => setForm({ ...form, ...patch });
  // Email is optional — validate the format only when something is entered. The error shows only
  // after the field is left or submit is attempted, not while typing.
  const [emailTouched, setEmailTouched] = useState(false);
  const emailError =
    form.email && !EMAIL_RE.test(form.email) ? "Enter a valid email address (e.g. name@example.com)" : null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (emailError) {
          setEmailTouched(true);
          return;
        }
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <PrefixInput prefix="Dr. " label="Full name" value={form.full_name} onChange={(v) => set({ full_name: v })} sanitize={(t) => t.replace(/[^A-Za-z. ]/g, "")} required />
        <PhoneInput label="Phone" value={form.phone} onChange={(v) => set({ phone: v })} required />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => set({ email: v.replace(/\s/g, "") })}
          onBlur={() => setEmailTouched(true)}
          error={emailTouched ? emailError : null}
        />
        <Input label="State medical council" value={form.state_medical_council} onChange={(v) => set({ state_medical_council: v })} required />
        <Input label="License number" value={form.license_number} onChange={(v) => set({ license_number: v })} required />
        <Input label="Specialisation" value={form.specialisation} onChange={(v) => set({ specialisation: v })} required />
      </div>

      <fieldset className="rounded-lg border p-3">
        <legend className="px-1 text-sm text-gray-500">Verification checklist</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {DOCTOR_CHECKLIST_ITEMS.map((item) => (
            <label key={item} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.verification_checklist[item] ?? false}
                onChange={(e) =>
                  set({ verification_checklist: { ...form.verification_checklist, [item]: e.target.checked } })
                }
              />
              {item.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </fieldset>

      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="flex gap-2">
        <button className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg" disabled={submitting}>
          Onboard doctor
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

function Input({
  label,
  value,
  onChange,
  onBlur,
  required,
  type = "text",
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  required?: boolean;
  type?: string;
  error?: string | null;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-gray-500">
        {label}
        {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        required={required}
        aria-invalid={!!error}
        className={`rounded-lg border px-3 py-1.5 outline-hidden focus:ring-1 ${
          error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "focus:border-brand focus:ring-brand"
        }`}
      />
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  );
}
